import { useState } from "react";
import { UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { getAutoroles, addAutorole, editAutorole, deleteAutorole } from "@/api/autoroles";
import { CrudCard, ListRow, IconButton } from "./CrudCard";
import { showApiError } from "./helpers";
import { ChoiceSelect, selectCls } from "./fields";

const TARGET_TYPES = ["human", "bot"];

export const Autoroles = ({ guildId, roles }) => {
  const { data, loading, error, refetch } = useApi(() => getAutoroles(guildId));
  const [roleId, setRoleId] = useState("");
  const [targetType, setTargetType] = useState("human");
  const [saving, setSaving] = useState(false);

  const items = data?.autoroles || [];
  const roleName = (id) => roles.find((r) => String(r.id) === String(id))?.name || `Role ${id}`;
  const selectableRoles = roles.filter((r) => !r.managed && r.name !== "@everyone");

  const add = async () => {
    if (!roleId) {
      toast.error("Pick a role first.");
      return;
    }
    setSaving(true);
    try {
      await addAutorole(guildId, { role_id: roleId, target_type: targetType });
      toast.success("Autorole added.");
      setRoleId("");
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const changeType = async (item, value) => {
    try {
      await editAutorole(guildId, item.role_id, { target_type: value });
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const remove = async (item) => {
    try {
      await deleteAutorole(guildId, item.role_id);
      toast.success("Autorole removed.");
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  return (
    <CrudCard
      icon={UserPlus}
      title="Autorole"
      description="Give roles automatically when members or bots join."
      testId="crud-autoroles"
      id="autoroles"
      loading={loading}
      error={error}
      onRetry={refetch}
      isEmpty={items.length === 0}
      emptyText="No autoroles configured."
    >
      <div className="mb-5 flex flex-col gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-4 sm:flex-row">
        <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className={`${selectCls} flex-1`} data-testid="autorole-add-role" aria-label="Role">
          <option value="">Select a role…</option>
          {selectableRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <select value={targetType} onChange={(e) => setTargetType(e.target.value)} className={selectCls} data-testid="autorole-add-type" aria-label="Applies to">
          {TARGET_TYPES.map((t) => (
            <option key={t} value={t}>
              {t === "human" ? "Members" : "Bots"}
            </option>
          ))}
        </select>
        <button
          onClick={add}
          disabled={saving}
          data-testid="autorole-add-submit"
          className="rounded-full bg-white px-5 py-2.5 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50"
        >
          {saving ? "Adding…" : "Add"}
        </button>
      </div>
      <ul className="space-y-3">
        {items.map((item) => (
          <ListRow
            key={item.role_id}
            testId={`autorole-row-${item.role_id}`}
            actions={
              <IconButton onClick={() => remove(item)} label="Delete" danger testId={`autorole-delete-${item.role_id}`}>
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </IconButton>
            }
          >
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-semibold text-paper-100">{roleName(item.role_id)}</p>
              <ChoiceSelect
                value={item.target_type || "human"}
                onChange={(v) => changeType(item, v)}
                options={TARGET_TYPES}
                testId={`autorole-type-${item.role_id}`}
              />
            </div>
          </ListRow>
        ))}
      </ul>
    </CrudCard>
  );
};
